import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut, User } from "firebase/auth";
import { UserProfile, UserRole } from "../types";
import { hasPermission as profileHasPermission, normalizeCustomPermissions, type PermissionKey } from "../lib/accessControl";

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  effectiveProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isStandard: boolean;
  hasPermission: (permissionKey: string) => boolean;
  attemptedEmail: string | null;
  previewRole: UserRole | null;
  isPreviewing: boolean;
  setPreviewRole: (role: UserRole | null) => void;
  clearPreviewRole: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  effectiveProfile: null,
  loading: true,
  error: null,
  attemptedEmail: null,
  login: async () => {},
  logout: async () => {},
  isAdmin: false,
  isStandard: false,
  hasPermission: () => false,
  previewRole: null,
  isPreviewing: false,
  setPreviewRole: () => {},
  clearPreviewRole: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [previewRole, setPreviewRoleState] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attemptedEmail, setAttemptedEmail] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setProfile(null);
        setPreviewRoleState(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setUser(currentUser);

        const email = currentUser.email;
        if (!email) throw new Error("No email associated with this account.");

        // Fetch profile from backend to get the assigned role
        let assignedRole: UserRole = "standard";
        let assignedStatus: UserProfile["status"] = "active";
        let customPermissions: string[] = [];
        try {
          const res = await fetch("/api/profile", {
            headers: {
              "x-user-uid": currentUser.uid,
              "x-user-email": currentUser.email || ""
            }
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.error || "access_denied");
          }
          if (data.role) assignedRole = data.role as UserRole;
          if (data.status) assignedStatus = data.status;
          customPermissions = normalizeCustomPermissions(data.customPermissions);
        } catch (e) {
          throw e;
        }
        
        if (assignedStatus !== "active") {
          throw new Error(assignedStatus);
        }
        
        const userProfile: UserProfile = {
          uid: currentUser.uid,
          email: email,
          displayName: currentUser.displayName || email.split('@')[0],
          role: assignedRole,
          status: assignedStatus,
          customPermissions,
          createdAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString()
        };

        setProfile(userProfile);
      } catch (err: any) {
        console.error("Access Control Error:", err);
        setError(err.message || "access_denied");
        setAttemptedEmail(currentUser?.email || null);
        setProfile(null);
        await signOut(auth); // Sign out if access is denied
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account consent'
    });
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed", error);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setProfile(null);
      setAttemptedEmail(null);
      setError(null);
      setPreviewRoleState(null);
      window.location.assign("/");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const setPreviewRole = (role: UserRole | null) => {
    if (profile?.role !== "admin" && profile?.role !== "super_admin") return;
    setPreviewRoleState(role);
  };

  const clearPreviewRole = () => {
    setPreviewRoleState(null);
  };

  const effectiveProfile =
    profile && (profile.role === "admin" || profile.role === "super_admin") && previewRole
      ? {
          ...profile,
          role: previewRole,
          status: "active" as UserProfile["status"],
          customPermissions: [],
        }
      : profile;

  const isAdmin = effectiveProfile?.role === "admin" || effectiveProfile?.role === "super_admin";
  const isStandard = effectiveProfile?.role === "standard" || (!isAdmin && user !== null);

  console.log("Auth Status:", {
    email: user?.email,
    role: profile?.role,
    effectiveRole: effectiveProfile?.role,
    isAdmin,
    isStandard,
    previewRole,
  });

  const hasPermission = (permissionKey: string) => {
    return profileHasPermission(effectiveProfile, permissionKey as PermissionKey);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        effectiveProfile,
        loading,
        error,
        attemptedEmail,
        login,
        logout,
        isAdmin,
        isStandard,
        hasPermission,
        previewRole,
        isPreviewing: Boolean(previewRole),
        setPreviewRole,
        clearPreviewRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
